/**
 * Typed domain errors.
 *
 * Domain code never throws a bare `Error`. Every failure carries a stable machine
 * code so the API can map it to an HTTP status without string-matching, and the UI
 * can show the user something specific in German rather than "Etwas ist schiefgelaufen".
 */

export type ErrorCode =
  // 400 — the request itself is malformed
  | "validation_failed"
  // 401 / 403
  | "unauthenticated"
  | "forbidden"
  | "tenant_mismatch"
  // 404
  | "not_found"
  // 409 — the request is well-formed but conflicts with current state
  | "conflict"
  | "invalid_state_transition"
  | "period_closed"
  | "already_released"
  // 422 — domain rules refuse the operation
  | "domain_rule_violated"
  | "unbalanced_settlement"
  | "insufficient_approval"
  | "four_eyes_required"
  // 429 / 5xx
  | "rate_limited"
  | "external_service_failed"
  | "internal";

const STATUS_BY_CODE: Record<ErrorCode, number> = {
  validation_failed: 400,
  unauthenticated: 401,
  forbidden: 403,
  tenant_mismatch: 403,
  not_found: 404,
  conflict: 409,
  invalid_state_transition: 409,
  period_closed: 409,
  already_released: 409,
  domain_rule_violated: 422,
  unbalanced_settlement: 422,
  insufficient_approval: 422,
  four_eyes_required: 422,
  rate_limited: 429,
  external_service_failed: 502,
  internal: 500,
};

export class KontorError extends Error {
  readonly code: ErrorCode;
  /** German message safe to show the user. */
  readonly userMessage: string;
  readonly details: Record<string, unknown>;

  constructor(code: ErrorCode, userMessage: string, details: Record<string, unknown> = {}, cause?: unknown) {
    super(`${code}: ${userMessage}`, cause === undefined ? undefined : { cause });
    this.name = "KontorError";
    this.code = code;
    this.userMessage = userMessage;
    this.details = details;
  }

  get httpStatus(): number {
    return STATUS_BY_CODE[this.code];
  }

  toJSON(): { code: ErrorCode; message: string; details: Record<string, unknown> } {
    return { code: this.code, message: this.userMessage, details: this.details };
  }
}

export function isKontorError(error: unknown): error is KontorError {
  return error instanceof KontorError;
}

export const notFound = (what: string, id?: string): KontorError =>
  new KontorError("not_found", `${what} wurde nicht gefunden.`, id ? { id } : {});

export const forbidden = (why = "Sie sind für diese Aktion nicht berechtigt."): KontorError =>
  new KontorError("forbidden", why);

export const tenantMismatch = (): KontorError =>
  new KontorError("tenant_mismatch", "Der Datensatz gehört nicht zu Ihrer Organisation.");

export const conflict = (why: string, details?: Record<string, unknown>): KontorError =>
  new KontorError("conflict", why, details);

export const invalidTransition = (entity: string, from: string, to: string): KontorError =>
  new KontorError(
    "invalid_state_transition",
    `${entity} kann nicht von „${from}“ auf „${to}“ wechseln.`,
    { from, to },
  );

export const periodClosed = (period: string): KontorError =>
  new KontorError("period_closed", `Die Periode ${period} ist abgeschlossen und kann nicht mehr verändert werden.`, { period });

export const domainRule = (why: string, details?: Record<string, unknown>): KontorError =>
  new KontorError("domain_rule_violated", why, details);

export const unbalanced = (what: string, difference: number): KontorError =>
  new KontorError(
    "unbalanced_settlement",
    `${what} geht nicht auf — Differenz von ${(difference / 100).toFixed(2)} €.`,
    { differenceCents: difference },
  );

export const fourEyesRequired = (what: string): KontorError =>
  new KontorError(
    "four_eyes_required",
    `${what} erfordert die Zeichnung durch zwei berechtigte Personen.`,
  );

export const externalFailed = (service: string, cause?: unknown): KontorError =>
  new KontorError("external_service_failed", `Der Dienst ${service} ist derzeit nicht erreichbar.`, { service }, cause);
